<script lang="ts" setup>
import { getMetaTags } from '~/utils/meta'
import type { CarouselI18nItem } from '~/types/elementsUi'
import { teamMemberImages } from '~/models/images'
import type { BreadcrumbItem } from '@nuxt/ui'
import { hardcodedFocusMunicipalities } from '~/models/hardcodedArrays'
import dayjs from 'dayjs'
import { pdfs } from '~/models/files'
import type { ImageProps } from '~/types/images'
import { heroImages } from '#shared/models/images'

definePageMeta({
  layout: false
})

defineRouteRules({
  prerender: true
})

const { t, tm, baseKey } = useI18n('pages.static.gemeente')

const heroImage = heroImages['ilse-en-frans']

useSeoMeta(
  getMetaTags(
    {
      title: '.meta.title',
      description: '.meta.description'
    },
    heroImage.src,
    baseKey
  )
)

useSchemaOrg(
  getSchemaOrg(
    {
      title: '.title',
      breadcrumbs: [{ name: '.title' }]
    },
    baseKey
  )
)

const today = dayjs()

const pdfLink1 = pdfs.infographic['projectplan-2025']
const pdfLink2 = pdfs.infographic['impactonderzoeken-2023-2024']

const florineImage: ImageProps = {
  src: teamMemberImages.florine,
  alt: {
    nl: 'Florine van der Vlies',
    en: 'Florine van der Vlies'
  }
}

const benefits = computed<CarouselI18nItem[]>(() => {
  return tm('.section-two.list')
})

const challenges = computed<CarouselI18nItem[]>(() => {
  return tm('.section-five.list')
})

const breadcrumbItems: BreadcrumbItem[] = [{ label: '.title', to: 'gemeente' }]
</script>

<template>
  <div>
    <NuxtLayout
      name="default"
      title=".title"
      description=".description"
      :image="heroImage"
      :ui="{
        image: 'lg:object-[center_-3rem]'
      }"
    >
      <template #buttons>
        <VButton
          to="https://calendly.com/florine-thuisgekookt"
          target="_blank"
          rel="noopener noreferrer"
          label=".button"
        />
      </template>

      <VBreadcrumb :items="breadcrumbItems" />

      <VSection title=".section-one.title">
        <VI18n key-path=".section-one.content">
          <template #tag>
            <strong> {{ t('.section-one.tag') }}</strong>
          </template>
        </VI18n>
      </VSection>

      <VSection title=".section-two.title">
        <template #content>
          <VCarousel
            v-slot="{ item }"
            :items="benefits"
            :number-of-items="{
              mobile: 1,
              tablet: 2,
              desktop: 3,
              widescreen: 4
            }"
            loop
            dots
            :ui="{ viewport: 'pb-4' }"
            sr-label=".section-two.carousel-aria-label"
          >
            <div class="text-center">
              <VIcon
                v-if="item?.icon"
                :icon="item.icon"
                size="large"
                font-awesome-icon-size="3x"
                class="mt-3 mb-6"
              />
              <h3 class="text-h3 font-bold">
                {{ item.title }}
              </h3>

              <p class="mt-3 mb-6">
                {{ item.description }}
              </p>
            </div>
          </VCarousel>
          <span class="block text-center italic">
            <VLink
              download="projectplan-2025"
              :href="pdfLink1"
              target="_blank"
              label=".section-two.resource-one"
            />
          </span>

          <span class="block text-center italic">
            <VLink
              download="impactonderzoeken-2023-2024"
              :href="pdfLink2"
              target="_blank"
              label=".section-two.resource-two"
            />
          </span>
        </template>
      </VSection>

      <VSection title=".section-three.title">
        <div class="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div class="flex-1">
            <p class="mb-6">
              {{ t('.section-three.content') }}
            </p>

            <div class="max-lg:text-center">
              <VButton
                to="https://calendly.com/florine-thuisgekookt"
                target="_blank"
                rel="noopener noreferrer"
                label=".section-three.button"
              />
            </div>
          </div>

          <figure class="mx-auto shrink-0 lg:mx-0">
            <VImage
              :image="florineImage"
              class="h-40 w-40 rounded-full object-cover shadow-sm lg:h-48 lg:w-48"
              sizes="lg:192px 160px"
            />
          </figure>
        </div>
      </VSection>

      <QuoteSection type="quote" title=".section-four.quote" class="pt-0" />

      <VSection title=".section-five.title">
        <template #content>
          <VCarousel
            v-slot="{ item }"
            :items="challenges"
            :number-of-items="{
              mobile: 1,
              tablet: 2,
              desktop: 3,
              widescreen: 4
            }"
            loop
            dots
            :ui="{ viewport: 'pb-4' }"
            sr-label=".section-five.carousel-aria-label"
          >
            <div class="text-center">
              <VIcon
                v-if="item?.icon"
                :icon="item.icon"
                size="large"
                font-awesome-icon-size="3x"
                class="mt-3 mb-6"
              />
              <h3 class="text-h3 font-bold">
                {{ item.title }}
              </h3>

              <p class="mt-3 mb-6">
                {{ item.description }}
              </p>
            </div>
          </VCarousel>
        </template>
      </VSection>

      <QuoteSection type="quote" title=".section-six.quote" class="pt-0" />

      <VSection>
        <template #title>
          {{
            t('.section-seven.title', {
              amount: hardcodedFocusMunicipalities.length,
              year: today.year()
            })
          }}
        </template>

        <p>
          {{ t('.section-seven.content') }}
          {{
            hardcodedFocusMunicipalities
              .map((municipality) => municipality.name)
              .join(', ')
          }}.
        </p>
      </VSection>

      <VSection title=".section-eighth.title" description=".section-eighth.content" />

      <TwoColorSection
        title=".section-nine.title"
        :ui="{
          from: 'from-pink',
          to: 'to-beige-800',
          mobile: 'max-lg:bg-pink',
          darkTo: 'dark:to-pure-black-700'
        }"
        icon="fa-hand-holding-heart"
        icon-color="dark-green-400"
        icon-dark-color="dark-green"
      >
        <p class="dark:text-pink-text">
          {{ t('.section-nine.content') }}
        </p>
      </TwoColorSection>

      <VSection title=".section-ten.title">
        <template #content>
          <div class="mx-auto prose flex max-w-section flex-col items-center text-center">
            <VButton
              to="https://calendly.com/florine-thuisgekookt"
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              label=".section-ten.button"
              class="mt-4"
            />

            <div class="mt-16 flex flex-col items-center gap-4">
              <VImage
                :image="florineImage"
                class="m-0! aspect-square h-40 w-40 rounded-full object-cover shadow-sm"
                sizes="160px"
              />

              <div class="flex flex-col items-center">
                <span class="text-h4 font-bold text-dark-green-400 dark:text-success"
                  >Florine van der Vlies</span
                >
                <div class="mt-4 flex flex-col items-center gap-x-6 gap-y-2">
                  <VButton
                    to="mailto:florine@thuisgekookt.nl"
                    icon="fa-envelope"
                    label="florine@thuisgekookt.nl"
                  />
                  <VButton to="tel:085-0608768" icon="fa-phone" label="085-0608768" />
                </div>
              </div>
            </div>
          </div>
        </template>
      </VSection>
    </NuxtLayout>
  </div>
</template>

<style scoped></style>
